import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getDeals } from '../api';
import DealCard from '../components/DealCard';
import SEO from '../components/SEO';
import './DealsPage.css';

const CATS = [
  { slug: '', name: 'All deals', icon: '🏷' },
  { slug: 'restaurants', name: 'Restaurants', icon: '🍽' },
  { slug: 'health-spa', name: 'Health & Spa', icon: '💆' },
  { slug: 'fitness', name: 'Fitness', icon: '🏋' },
  { slug: 'retail', name: 'Retail', icon: '🛍' },
  { slug: 'home-services', name: 'Home Services', icon: '🔧' },
  { slug: 'dental-medical', name: 'Dental & Medical', icon: '🦷' },
  { slug: 'beauty', name: 'Beauty', icon: '💇' },
  { slug: 'auto', name: 'Auto', icon: '🚗' },
  { slug: 'entertainment', name: 'Entertainment', icon: '🎭' },
];

const HOODS = ['', 'Downtown', 'St. Vital', 'River Heights', 'Transcona', 'North End', 'Osborne', 'Wolseley', 'West End'];

export default function DealsPage() {
  const [params, setParams] = useSearchParams();
  const [deals, setDeals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get('search') || '');
  const [category, setCategory] = useState(params.get('category') || '');
  const [neighbourhood, setNeighbourhood] = useState('');
  const [page, setPage] = useState(0);
  const PER = 12;

  const load = useCallback(() => {
    setLoading(true);
    const q = { limit: PER, offset: page * PER };
    if (category) q.category = category;
    if (neighbourhood) q.neighbourhood = neighbourhood;
    if (search) q.search = search;
    getDeals(q)
      .then(d => { setDeals(d.deals); setTotal(d.total); })
      .catch(() => setDeals([]))
      .finally(() => setLoading(false));
  }, [category, neighbourhood, search, page]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => { e.preventDefault(); setPage(0); load(); };

  return (
    <div className="deals-page">
      <SEO
        title="Browse Restaurant Deals in Winnipeg — Up to 50% Off"
        description={`Find the best restaurant deals in Winnipeg — up to 50% off. Filter by cuisine, neighbourhood, and more. ${total > 0 ? `${total} deals available right now.` : ''}`}
        url="/deals"
      />
      <div className="deals-header">
        <div className="container">
          <h1 className="font-display deals-page-title">Local deals in Winnipeg</h1>
          <form className="deals-search-bar" onSubmit={handleSearch}>
            <input
              className="form-control"
              placeholder="Search deals or businesses…"
              value={search}
              onChange={e => { setSearch(e.target.value); if (!e.target.value) setPage(0); }}
            />
            <select
              className="form-control"
              value={neighbourhood}
              onChange={e => { setNeighbourhood(e.target.value); setPage(0); }}
            >
              {HOODS.map(h => <option key={h} value={h}>{h || 'All neighbourhoods'}</option>)}
            </select>
            <button type="submit" className="btn btn-primary">Search</button>
          </form>
        </div>
      </div>

      <div className="container deals-body">
        {/* Category filter */}
        <div className="cat-filter-bar">
          {CATS.map(c => (
            <button
              key={c.slug}
              className={`cat-filter-btn ${category === c.slug ? 'active' : ''}`}
              onClick={() => { setCategory(c.slug); setPage(0); }}
            >
              {c.icon} {c.name}
            </button>
          ))}
        </div>

        {/* Results */}
        <div className="deals-results">
          <p className="deals-count">
            {loading ? 'Loading…' : `${total} deal${total !== 1 ? 's' : ''} found`}
          </p>
          {loading ? (
            <div className="deals-grid">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="deal-skeleton card">
                  <div className="skeleton" style={{ height: 140 }} />
                  <div style={{ padding: 14 }}>
                    <div className="skeleton mb-1" style={{ height: 12, width: '60%' }} />
                    <div className="skeleton mb-1" style={{ height: 16, width: '90%' }} />
                    <div className="skeleton" style={{ height: 12, width: '40%' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : deals.length > 0 ? (
            <>
              <div className="deals-grid">
                {deals.map(d => <DealCard key={d.id} deal={d} />)}
              </div>
              {total > PER && (
                <div className="pagination">
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                  >← Previous</button>
                  <span className="page-info">Page {page + 1} of {Math.ceil(total / PER)}</span>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={(page + 1) * PER >= total}
                    onClick={() => setPage(p => p + 1)}
                  >Next →</button>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <p style={{ fontSize: 48, marginBottom: 12 }}>🔍</p>
              <p>No deals found for these filters.</p>
              <button className="btn btn-ghost btn-sm mt-2" onClick={() => { setCategory(''); setSearch(''); setNeighbourhood(''); }}>
                Clear filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
